import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Query parameter the Edara Portal appends to the frame URL to request embedded mode.
 * Its presence alone is NOT enough — see [[resolveEmbedContext]].
 */
const EMBED_PARAM = 'embed';
const EMBED_PARAM_VALUE = 'portal';

/**
 * sessionStorage key under which a CONFIRMED embedding is remembered, holding the portal origin
 * that was validated on the entry load.
 *
 * The parameter only exists on the URL the portal loads. Every later navigation — router links,
 * and above all the full-document OIDC `/callback` round-trip — drops it, so the decision has to
 * be remembered. sessionStorage is scoped to the browsing context AND the origin, so the copy
 * written inside the portal's frame is invisible to a normal tab on the same origin: opening
 * Edara 3 directly can never inherit embedded mode from a frame open elsewhere.
 */
const EMBED_SESSION_KEY = 'edara.embedPortalOrigin';

/** Shape of the resolved embedding context, computed once per document. */
export interface EmbedContext {
  /** True only when this document is genuinely running inside an allowed portal frame. */
  readonly embedded: boolean;
  /** Exact origin of the embedding portal — the only target we ever postMessage to. */
  readonly portalOrigin: string | null;
}

/**
 * Resolves whether this document is running as an embedded product inside the Edara Portal.
 *
 * Three conditions must ALL hold, so that a stray parameter, a stale session value or a hostile
 * framer can never put a normally-opened app into embedded mode:
 *
 *  1. the document is framed (`window.self !== window.top`);
 *  2. the portal asked for it — `?embed=portal` on the entry URL, or an embedding already
 *     confirmed earlier in this frame's session;
 *  3. the embedding origin is on the allowlist in `environment.portalOrigins`.
 *
 * Condition 3 is what makes this safe to key security-relevant behaviour off.
 */
function resolveEmbedContext(): EmbedContext {
  const notEmbedded: EmbedContext = { embedded: false, portalOrigin: null };

  // 1. Not framed → never embedded, whatever the URL says.
  if (window.self === window.top) {
    return notEmbedded;
  }

  // 2. Requested by the portal, now or earlier in this frame's session.
  const requested = new URLSearchParams(window.location.search).get(EMBED_PARAM) === EMBED_PARAM_VALUE;
  const remembered = readRememberedOrigin();
  if (!requested && !remembered) {
    return notEmbedded;
  }

  // 3. The embedder must be a known portal origin.
  const portalOrigin = resolveEmbedderOrigin() ?? remembered;
  if (!portalOrigin || !environment.portalOrigins.includes(portalOrigin)) {
    console.warn(`[embed] embedded mode refused: '${portalOrigin ?? 'unknown'}' is not an allowed portal origin.`);
    return notEmbedded;
  }

  if (remembered !== portalOrigin) {
    try {
      sessionStorage.setItem(EMBED_SESSION_KEY, portalOrigin);
    } catch {
      // Storage can be unavailable (partitioned/blocked). The parameter alone still carries the
      // first load; later navigations simply fall back to standalone rendering.
    }
  }

  return { embedded: true, portalOrigin };
}

/** The portal origin confirmed earlier in this frame's session, if it is still allowlisted. */
function readRememberedOrigin(): string | null {
  try {
    const stored = sessionStorage.getItem(EMBED_SESSION_KEY);
    return stored && environment.portalOrigins.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Origin of the document that framed this one, or null when it cannot be determined here.
 *
 * `ancestorOrigins` is the authoritative source — it reflects the actual frame tree and the framer
 * cannot spoof it — but only Chromium and WebKit implement it. Firefox has neither, leaving
 * `document.referrer`, which names the document that LINKED here rather than the one framing us:
 * accurate on the portal's own initial load, but wrong after the OIDC round-trip, where it is
 * Keycloak. That is exactly the case the remembered origin covers, so returning null here is a
 * "don't know", not a "not embedded" — see the caller.
 */
function resolveEmbedderOrigin(): string | null {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors?.length) {
    // The LAST entry is the top-level document; the first is our immediate parent. The portal
    // frames products directly, so parent and top are the same document.
    return ancestors[ancestors.length - 1];
  }

  if (document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      // Only usable as the embedder when it IS a portal; otherwise fall through to the remembered
      // origin rather than rejecting an embedding that a redirect chain has obscured.
      return environment.portalOrigins.includes(referrerOrigin) ? referrerOrigin : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * The embedding decision, resolved ONCE at module load — before Angular bootstraps and
 * therefore before any APP_INITIALIZER (Keycloak, translations) or guard can run. Those
 * consumers need the answer earlier than DI can give it, and the answer can never change
 * during the life of a document.
 */
export const EMBED_CONTEXT: EmbedContext = resolveEmbedContext();

// Mark the document as embedded IMMEDIATELY, at module load, rather than waiting for
// AppService.init() during bootstrap.
//
// `body` reserves the top bar's height through `--header-height`, and `body.embedded` is what
// zeroes it. Adding the class only once Angular has bootstrapped leaves a window — long on a cold
// load, where the bundle is still being parsed — in which the app reserves space for a top bar it
// will never draw, so the whole page sits ~90px too low and then jumps up. The embedding decision
// is already known here, before bootstrap, so there is no reason to wait.
if (EMBED_CONTEXT.embedded) {
  // Angular's bundles are module scripts, so they run deferred and <body> exists by now. The
  // fallback covers any change to how the app is loaded: this class is the ONLY thing that
  // applies embedded layout, so failing to set it must not be silent.
  if (document.body) {
    document.body.classList.add('embedded');
  } else {
    document.addEventListener('DOMContentLoaded', () => document.body.classList.add('embedded'), { once: true });
  }
}

/**
 * Tells the app whether it is rendering standalone or as an embedded product inside the
 * Edara Portal, which supplies its own top bar and side navigation.
 *
 * This is the single flag behind every embedded-mode difference. When it is false — the
 * only possibility for a normally-opened app — nothing anywhere behaves differently.
 */
@Injectable({ providedIn: 'root' })
export class EmbedService {

  /** True when running inside an allowed Edara Portal frame. */
  public get isEmbedded(): boolean {
    return EMBED_CONTEXT.embedded;
  }

  /** The portal's exact origin, or null when not embedded. Never postMessage to '*'. */
  public get portalOrigin(): string | null {
    return EMBED_CONTEXT.portalOrigin;
  }

  /**
   * Reads a value the portal passed on the frame URL (`lang`, `theme`, `oidcOrg`).
   * Always null when not embedded, so callers can use it unconditionally.
   */
  public param(name: string): string | null {
    if (!this.isEmbedded) return null;
    return new URLSearchParams(window.location.search).get(name);
  }
}
